import React from 'react';
import { Box } from '@chakra-ui/react';

const config = {
    loader: {
        load: [
            '[tex]/boldsymbol',
            '[tex]/physics',
            '[tex]/ams',
            '[tex]/mhchem',
            '[tex]/html',
            '[tex]/newcommand',
            '[tex]/upgreek',
            '[tex]/unicode',
            '[tex]/tagformat',
            '[tex]/extpfeil',
            '[tex]/empheq',
            '[tex]/gensymb',
            '[tex]/mathtools',
            '[tex]/textmacros',
            '[tex]/textcomp',
        ],
    },
    tex: {
        packages: {
            '[+]': [
                'boldsymbol',
                'html',
                'physics',
                'ams',
                'mhchem',
                'newcommand',
                'upgreek',
                'unicode',
                'tagformat',
                'extpfeil',
                'empheq',
                'gensymb',
                'mathtools',
                'textmacros',
                'textcomp',
            ],
        },
        inlineMath: [['\\(', '\\)']],
        displayMath: [
            ['$$', '$$'],
            ['\\[', '\\]'],
        ],
        physics: {
            italicdiff: false,
            arrowdel: false,
        },
        ams: {
            multlineWidth: '100%',
            multlineIndent: '1em',
        },
        unicode: {
            fonts: "STIXGeneral, 'Arial Unicode MS'",
        },
        processEscapes: true,
        macros: {
            bm: ['\\boldsymbol{#1}', 1],
            rightleftharpoonup: '\\Newextarrow{\\rightleftharpoonup}{10,10}{0x21CC}',
            longleftharp: '\\Newextarrow{\\longleftharp}{10,10}{0x21BD}',
            longrightharp: '\\Newextarrow{\\longrightharp}{10,10}{0x21C0}',
            aggrg: ['\\mskip2mu(\\hspace{-0.035em}\\textit{#1}\\hspace{0.035em})', 1],
            aq: '\\aggrg{aq}',
            sd: '\\aggrg{s}',
            lq: '{\\aggrg{l}}',
            gs: '\\aggrg{g}',
            cpaggrg: ['\\small\\mskip2mu(\\textit{#1})', 1],
            cpaq: '\\cpaggrg{aq\\hspace{-0.015em}}',
            cpsd: '\\cpaggrg{\\hspace{-0.01em}s\\hspace{0.04em}}',
            cplq: '\\cpaggrg{l\\hspace{0.04em}}',
            cpgs: '\\cpaggrg{g}',
            degC: '\\degree\\!C',
            degF: '\\degree\\!F',
            tempC: ['\\pu{#1 \\degC}', 1],
            tempF: ['\\pu{#1 \\degF}', 1],
            degCmath: '\\degree\\!\\mathrm{C}',
            degFmath: '\\degree\\!\\mathrm{F}',
            pfxex: ['\\text{#1} = \\pu{#2}\\ (\\pu{#3})', 3],
            tripeq: ['\\pu{#1} = \\pu{#2} = \\pu{#3}', 3],
            hairspace: '\\unicode{x200A}',
            gcc: ['\\pu{#1 g/cm3}', 1],
            gL: ['\\pu{#1 g/L}', 1],
            unitrange: ['#1\\textendash#2\\;\\pu{#3}', 3],
            simplerel: ['\\textit{#1} = \\frac{\\textit{#2}}{\\textit{#3}}', 3],
            pufrac: ['\\frac{\\pu{#1}}{\\pu{#2}}', 2],
            jfrac: ['\\frac{#1}{#2}', 2],
            jsupfrac: ['\\frac{#1}{#2}', 2],
            jint: ['\\int_{#1}^{#2}', 2],
            liparens: ['(#1)', 1],
            unitequiv: ['\\pu{#1} = \\pu{#2}', 2],
            unitconv: ['\\pu{#1} \\times \\pufrac{#2}{#3} = \\pu{#4}', 4],
            unitappx: ['\\pu{#1} \\approx \\pu{#2}', 2],
            equiltxt: ['\\mathrel{\\raise{0.08em}{\\longrightharp{#1}}\\llap{\\lower{0.08em}{\\longleftharp[#2]{}\\hspace{0.05em}}}}', 2],
            equil: '\\equiltxt{\\hphantom{xx}}{\\hphantom{xx}}',
            unitconvparen: ['\\pu{#1} \\times \\left(\\pufrac{#2}{#3}\\right) = \\pu{#4}', 4],
            ratio: ['#1\\hairspace\\unicode{x2236}\\hairspace#2', 2],
            cestrike: ['\\enclose{horizontalstrike}{\\ce{#1}}', 1],
            tratio: ['\\ratio{\\text{#1}}{\\text{#2}}', 2],
            tripratio: ['#1\\hairspace\\unicode{x2236}\\hairspace#2\\hairspace\\unicode{x2236}\\hairspace#3', 3],
            triptratio: ['\\tripratio{\\text{#1}}{\\text{#2}}{\\text{#3}}', 3],
            angstrom: '\\unicode{x00C5}',
            nuclide: ['\\ce{^{#1}_{#2}{#3}}', 3],
            quadratio: [
                '#1\\hairspace\\unicode{x2236}\\hairspace#2\\hairspace\\unicode{x2236}\\hairspace#3\\hairspace\\unicode{x2236}\\hairspace#4',
                4,
            ],
            negfullspace: '\\hspace{-0.25em}',
            pctelem: ['\\pu{#1\\negfullspace\\% #2}', 2],
            deltaplus: '{\\updelta+}',
            smallcapsD: '\\unicode{x1D05}',
            smallcapsL: '\\unicode{x029F}',
            betaminus: '\\ce{\\upbeta^-}',
            betaplus: '\\ce{\\upbeta^+}',
            boldbetaminus: '\\ce{\\textbf{\\unicode{x03B2}}^{\\textbf{\\unicode{x2212}}}}',
            boldbetaplus: '\\ce{\\textbf{\\unicode{x03B2}}^{\\textbf{+}}}',
            boldhe: '\\nuclide{\\textbf{4}}{\\textbf{2}}{\\textbf{He}}',
            boldalpha: '\\nuclide{\\textbf{4}}{\\textbf{2}}{\\textbf{\\unicode{x03B1}}}',
            alphaparticle: '\\ce{^4_2\\upalpha}',
            ccratio: '\\ce{^14_6C}\\mathord{:}\\!\\ce{^12_6C}',
            dV: '\\Updelta\\negthinspace V',
            dS: '\\Updelta\\hspace{-0.05em} S',
            dH: '\\Updelta\\hspace{-0.05em} H',
            dG: '\\Updelta\\hspace{-0.05em} G',
            dSstd: '\\dS\\hspace{-0.1em}^\\circ',
            Sstd: 'S\\hspace{-0.1em}^\\circ',
            dGstd: '\\dG\\hspace{-0.1em}^\\circ',
            dGstdf: '\\dGstd_\\mathrm{f}',
            dU: '\\Updelta\\hspace{-0.07em} U',
            qsub: ['\\mathord{q\\hairspace_\\mathrm{#1}}', 1],
            lnpn: ['\\ln{\\hspace{-0.1em}(\\hspace{-0.05em}#1)}', 1],
            lnbigpn: ['\\ln\\negmedspace\\left(\\hspace{-0.07em}#1\\hspace{-0.07em}\\right)', 1],
            Sstdsubce: ['\\Sstd_{\\hspace{0.05em}\\ce{#1\\hspace{0.08em}($#2\\hspace{0.05em}$)}}', 2],
            dHstd: '\\dH\\hspace{-0.13em}^\\circ',
            dHstdsub: ['\\dHstd_\\mathrm{#1}', 1],
            dGstdfwat: ['\\dGstd_\\mathrm{f_\\mathrm{H_{\\tiny2\\hairspace}O\\hairspace(\\mathnormal{#1}\\hspace{0.07em})}}', 1],
            Qc: 'Q\\hspace{-0.09em}_c',
            Qp: 'Q\\hspace{-0.09em}_p',
            cefrac: ['\\frac{\\ce{#1}}{\\ce{#2}}', 2],
            Psubce: ['P_\\ce{#1}', 1],
            fakevertbuffer: '\\vphantom{\\displaystyle{\\frac{1}{2}}}',
            Esub: ['E\\hspace{-0.12em}_\\mathrm{#1}', 1],
            Ecell: '\\Esub{cell}',
            EX: '\\Esub{X}',
            Estd: 'E^{\\hspace{-0.13em}\\circ}',
            Estdsub: ['\\Estd_\\mathrm{#1}', 1],
            Estdcell: '\\Estdsub{cell}',
            EstdX: '\\Estdsub{X}',
            Estdcat: '\\Estdsub{cathode}',
            Estdan: '\\Estdsub{anode}',
            Ecat: '\\Esub{cathode}',
            Ean: '\\Esub{anode}',
            cellnotation: [
                '\\ce{#1} \\mathrel{\\empheqbiglvert} \\ce{#2} \\hspace{0.2778em}\\empheqbiglVert\\hspace{0.2778em} \\ce{#3} \\mathrel{\\empheqbiglvert} \\ce{#4}',
                4,
            ],
            logten: '\\log_{10}',
            logflexpn: ['\\log\\hspace{-0.08em}{\\left(#1\\right)}', 1],
            Qr: 'Q\\hspace{-0.06em}_\\mathrm{r}',
            Ksubup: ['K\\!_\\mathrm{#1}', 1],
            Ka: '\\Ksubup{a}',
            Kb: '\\Ksubup{b}',
            pKa: '\\mathrm{p}\\Ka',
            pKb: '\\mathrm{p}\\Kb',
            Ksp: '\\Ksubup{sp}',
            pH: '\\text{pH}',
            pOH: '\\text{pOH}',
            hhece: ['{#1} = {#2} + \\logflexpn{\\cefrac{#3}{#4}}', 4],
            hhe: ['{#1} = {#2} + \\pu{log{\\frac{#3}{#4}}}', 4],
            Kf: '\\Ksubup{f}',
            faketableline: '\\hline\\fakevertbuffer',
            faketable: ['\\bbox[border:1px solid black]{\\begin{gather*}\\fakevertbuffer#1\\end{gather*}}', 1],
            Qsp: 'Q\\hspace{-0.03em}_\\mathrm{sp}',
            Ksubce: ['K\\!_\\ce{#1}', 1],
            Kd: '\\Ksubup{d}',
            Kw: '\\Ksubup{w}',
            pKw: '\\mathrm{p}\\Kw',
            Vtitrant: 'V_\\mathrm{\\hspace{0.05em}titrant}',
            smallunderbuffer: '\\lower{0.3em}{\\vphantom{j_{\\large j_j}}}',
            pHcalc: [
                `\\begin{align}
            \\ce{[H3O+]} &= \\frac{n_\\ce{H3O+}}{V_\\mathrm{sample} + \\Vtitrant}\\\\
            &=\\frac{(\\pu{#1}) - \\left[\\left(\\pu{#2}\\right)(\\pu{#3})\\right]}{(\\pu{#4}) + (\\pu{#3})}\\\\
            &= \\pu{#5 M}\\smallunderbuffer\\\\
            \\pH &= -\\log\\ce{[H3O+]}\\\\
            &= -\\logflexpn{\\pu{#5}}\\\\
            &= #6
            \\end{align}`,
                6,
            ],
            pHpOHcalc: [
                `
	        \\begin{align}
	        \\ce{[OH-]} &= \\frac{n_\\ce{OH-}}{V_\\mathrm{sample} + \\Vtitrant}\\\\
	        &= \\frac{\\left[\\left(\\pu{#1}\\right)(\\pu{#2})\\right] - (\\pu{#3})}{(\\pu{#2}) + (\\pu{#4})}\\\\
	        &= \\pu{#5 M}\\smallunderbuffer\\\\
	        \\pOH &= -\\log\\ce{[OH−]}\\\\
	        &= -\\logflexpn{\\pu{#5}}\\\\
	        &= #6\\smallunderbuffer\\\\
	        \\pH &= 14 - \\pOH\\\\
	        &=14 - #6\\\\
	        &=#7
	        \\end{align}
	        `,
                7,
            ],
            cecancel: ['\\cancel{\\ce{#1}}', 1],
            Kc: 'K\\negthinspace_c',
            Kcsub: ['{\\Kc}_{_{#1}}', 1],
            Kcfwd: '\\Kcsub{\\mathrm{fwd}}',
            Kcrev: '\\Kcsub{\\mathrm{rev}}',
            Kcpr: 'K\\prime\\negthickspace\\negthinspace_{c}',
            Kcprsub: ['{\\Kcpr}_{_{#1}}', 1],
            Kcprfwd: '\\Kcprsub{\\mathrm{fwd}}',
            Kcprrev: '\\Kcprsub{\\mathrm{rev}}',
            threehalves: '\\tfrac{3}{2}\\hspace{0.04em}',
            onehalf: '\\tfrac{1}{2}\\hspace{0.04em}',
            fillblank: '\\underline{\\qquad}{\\fakevertbuffer}{\\vphantom{\\displaystyle{\\frac{1}{2}}}}',
            smallsqrt: ['\\textstyle{\\sqrt{\\mathstrut{#1}}}', 1],
            Kp: 'K\\negthinspace_p',
            KpKcrel: ['\\Kp=\\Kc\\hairspace(RT\\hairspace)^{#1}', 1],
            Qppr: 'Q\\hairspace\\prime\\negthickspace\\negthinspace\\hspace{0.09em}_{p}',
            Ea: 'E\\!_\\mathrm{a}',
            ccovertime: ['\\frac{\\Updelta\\ce{[#1]}}{\\Updelta t}', 1],
            At: '\\ce{[A]}_t',
            Anought: '\\ce{[A]}_0',
            halflife: 't_{1/2}',
            lineqn: 'y=mx+b',
            dHsub: ['\\dH\\hspace{-0.13em}_\\mathrm{#1}', 1],
            perdegC: '\\degC^{-1}',
            jgcval: ['\\pu{#1 J g-1 \\perdegC}', 1],
            dT: '\\Updelta\\negthinspace T',
            angstromword: '\\text{\\unicode{x00E5}ngstr\\unicode{x00F6}m}',
            braggeqn: 'n\\lambda = 2d\\sin\\hspace{-0.03em}(\\hspace{-0.12em}\\theta\\hspace{0.08em})',
            angstromwordpl: '\\text{\\unicode{x00E5}ngstr\\unicode{x00F6}ms}',
            deltaminus: '{\\updelta-}',
            sphyb: ['sp\\hspace{0.08em}^{#1}', 1],
            spdhyb: ['\\sphyb{#1}\\hspace{-0.06em}d\\hspace{0.08em}^{#2}', 2],
            dHstdf: '\\dHstd_\\mathrm{f}',
            dHstdlattice: '\\dHstdsub{lattice}',
            Zeff: 'Z_\\mathrm{eff}',
            Eea: 'E\\hspace{-0.12em}_\\mathrm{ea}',
            Ei: '\\Esub{i}',
            ufinal: 'U_\\mathrm{final}',
            uinitial: 'U_\\mathrm{initial}',
            jgc: '\\pu{J g-1 \\perdegC}',
            cspcalc: [
                `\\begin{align}
                c_\\mathrm{#1} &= \\frac{\\pu{\#2 J}}{(\\pu{\#3 g})(\\pu{\#4 \\degC})}\\\\
                &= \\jgcval{#5}
                \\end{align}`,
                5,
            ],
            tfinal: 'T_\\mathrm{final}',
            tinitial: 'T_\\mathrm{initial}',
            tempchange: '\\tfinal - \\tinitial',
            strikeunit: ['\\mathord{\\enclose{horizontalstrike}{\\pu{#1}\\vphantom{^{-1}}}}', 1],
            msubthinsp: ['m\\hspace{-0.06em}_\\mathrm{#1}', 1],
            Rsubup: ['R\\mskip-1mu_\\mathrm{#1}', 1],
            Esubit: ['E\\hspace{-0.12em}_{#1}', 1],
            vecu: '\\skew4\\vec u',
            vecp: '\\skew5\\vec p',
            abspsisq: '\\abs{\\mskip-2mu\\textit{\\unicode{x03A8}}\\mskip1mu}^2',
            hamiltonian: '\\mathsfit{\\unicode{x0124}}',
            nsubup: ['n_\\mathrm{#1}', 1],
            slantell: '{\\large\\mathscr{l}}',
            msubell: '\\msubthinsp{\\smash{\\mskip-1mu\\slantell}}',
            msubs: 'm\\hspace{-0.1em}_s',
            cis: '\\textit{cis}',
            trans: '\\textit{trans}',
            fac: '\\textit{fac}',
            mer: '\\textit{mer}',
            threetwopufrac: [
                '\\frac{\\left(\\pu{#1}\\right)\\left(\\pu{#2}\\right)\\left(\\pu{#3}\\right)}{\\left(\\pu{#4}\\right)\\left(\\pu{#5}\\right)}',
                5,
            ],
            serifi: '\\unicode{x1D456}',
            Psubup: ['P_\\mathrm{#1}', 1],
            Ptotal: '\\Psubup{total}',
            psubce: ['p_\\ce{\\hairspace #1}', 1],
            threeonepufrac: ['\\frac{\\left(\\pu{#1}\\right)\\left(\\pu{#2}\\right)\\left(\\pu{#3}\\right)}{\\pu{#4}}', 4],
            ysubup: ['y_\\mathrm{#1}', 1],
            ntotal: '\\nsubup{total}',
            ysubce: ['y_\\ce{#1}', 1],
            nsubce: ['n_\\ce{#1}', 1],
            psubup: ['p_\\mathrm{#1}', 1],
            urms: 'u_\\mathrm{rms}',
            vecFsub: ['\\skew4\\vec F_\\mathrm{#1}', 1],
            NsubA: 'N\\mskip-4mu_\\mathrm{A}',
            csubup: ['c_\\mathrm{#1}', 1],
            kH: 'k_\\mathrm{H}',
            csubce: ['c_{\\ce{#1}}', 1],
            msubup: ['m_\\mathrm{#1}', 1],
            msubce: ['m_\\ce{#1}', 1],
            xsubup: ['x_\\mathrm{#1}', 1],
            molal: '\\mathscr{m}',
            xsubce: ['x_\\ce{#1}', 1],
            pstarsubup: ['p^{\\lower{0.08em}{\\star}}_{\\smash{\\mathrm{#1}}}', 1],
            pstarsubce: ['p^{\\lower{0.08em}{\\star}}_{\\smash{\\ce{#1}}}', 1],
            Kbebul: '\\Ksubup{b}',
            Kfcryo: '\\Ksubup{f}',
        },
    },
    chtml: {
        scale: 0.8, // global scaling factor for all expressions
        minScale: 0.8, // smallest scaling factor to use
        mtextInheritFont: true, // true to make mtext elements use surrounding font
        merrorInheritFont: false, // true to make merror text use surrounding font
        mtextFont: 'LMSans10', // font to use for mtext, if not inheriting (empty means use MathJax fonts)
        merrorFont: 'serif', // font to use for merror, if not inheriting (empty means use MathJax fonts)
        unknownFamily: 'CMU Sans Serif', // font to use for character that aren't in MathJax's fonts
        mathmlSpacing: false, // true for MathML spacing rules, false for TeX rules
        skipAttributes: {}, // RFDa and other attributes NOT to copy to the output
        exFactor: 0.5, // default size of ex in em units
        displayAlign: 'center', // default for indentalign when set to 'auto'
        displayIndent: '0', // default for indentshift when set to 'auto'
    },
    options: {
        // Keep MathJax out of form controls — injecting <mjx-container> into <option>/<input>
        // breaks dropdown and fill-in-the-blank question types.
        skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code', 'annotation', 'annotation-xml', 'select', 'option', 'input'],
    },
    startup: {
        typeset: false,
    },
    'HTML-CSS': { linebreaks: { automatic: true }, preferredFont: 'STIX', availableFonts: ['STIX'], webFont: 'STIX-Web' },
    SVG: { linebreaks: { automatic: true }, font: 'STIX-Web' },
};


export const MATHJAX_CONFIG = config;

export const MATHJAX_SRC = 'https://cdn.jsdelivr.net/npm/mathjax@3.2.2/es5/tex-mml-chtml.js';

export const MATH_CONTENT_PATTERN = /(\$\$|\\\(|\\\[|\\begin\{|<math[\s>]|ql-formula)/;

export const contentHasMath = (text = ''): boolean => MATH_CONTENT_PATTERN.test(text);


export const MacroBootstrap = () => (
    <Box
        dangerouslySetInnerHTML={{
            __html: `$$\\rightleftharpoonup{xx}$$
                 $$\\longleftharp{xx}$$,
                 $$\\longrightharp{xx}$$,
                `,
        }}
        display="none"
    />
);


